import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Card, Checkbox, Form, Input, Radio, Space, Spin, Tag, Typography, message } from "antd";
import { CheckCircleOutlined, ClockCircleOutlined, FormOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useParams, useSearchParams } from "react-router-dom";
import nodeAPI from "../../service/ApiServiceNode";
import { Button } from "../../design-system";
import logoImage from "../../assets/logo-mediatropy-2024-name.gif";
import "./survey.scss";

const { Paragraph, Text, Title } = Typography;

const RATING_SCALE = Array.from({ length: 11 }, (_, score) => score);
const FIVE_POINT_RATING_GUIDE = "1 = very low, 5 = very high";

const isOtherOption = (option) => String(option || "").trim().toLowerCase().startsWith("other");

const getOtherAnswerKey = (questionId) => `${questionId}__other_text`;

const getSurveyEndDate = (survey) => survey?.end_at || survey?.end_date || null;
const getSurveyStartDate = (survey) => survey?.start_at || null;

const parseJsonObject = (value) => {
    if (!value) return {};
    if (typeof value === "object") return value;

    try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch (_error) {
        return {};
    }
};

const getQuestionOptions = (question) => {
    if (Array.isArray(question?.options_json)) {
        return question.options_json;
    }
    if (typeof question?.options_json === "string") {
        try {
            const parsed = JSON.parse(question.options_json);
            return Array.isArray(parsed) ? parsed : [];
        } catch (_error) {
            return [];
        }
    }
    return [];
};

const getMaxSelections = (question) => {
    const settings = parseJsonObject(question?.settings_json);
    const value = parseInt(settings.max_selections, 10);
    return Number.isInteger(value) && value > 0 ? value : null;
};

const isFivePointScaleQuestion = (question) => {
    if (question?.question_type !== "single_choice") {
        return false;
    }

    const options = getQuestionOptions(question).map((option) => String(option || "").trim());
    return options.length === 5 && options.every((option, index) => option === String(index + 1));
};

const getQuestionHelpText = (question, isInvalid, hasMissingOtherText) => {
    if (isInvalid && hasMissingOtherText) {
        return "Please add details for Other.";
    }

    if (isInvalid) {
        return "This question needs an answer.";
    }

    if (isFivePointScaleQuestion(question)) {
        return FIVE_POINT_RATING_GUIDE;
    }

    return question.helper_text;
};

const formatSurveyDateTime = (value) => {
    if (!value) return "No deadline";

    const date = dayjs(value);
    if (!date.isValid()) return "No deadline";

    return date.format("D MMM YYYY, HH:mm");
};

const buildPreviewSurveyRecipient = (payload, previewRecipientName) => {
    const recipient = payload?.surveyRecipient;
    if (recipient?.survey) {
        return {
            ...recipient,
            respondent_name: previewRecipientName || recipient.respondent_name || "Preview recipient",
        };
    }

    const survey = payload?.survey || null;
    if (!survey) {
        return null;
    }

    return {
        id: null,
        survey_id: survey.id,
        status: "preview",
        invited_at: null,
        responded_at: null,
        respondent_name: previewRecipientName || "Preview recipient",
        survey: {
            id: survey.id,
            title: survey.title,
            description: survey.description,
            is_anonymous: survey.is_anonymous,
            status: survey.status,
            start_at: survey.start_at,
            end_at: survey.end_at,
            survey_question: survey.survey_question,
        },
    };
};

const getSurveyAvailability = (survey) => {
    if (!survey) {
        return { state: "unknown", canSubmit: false, message: "Survey link not found." };
    }

    const now = dayjs();
    const startAt = getSurveyStartDate(survey) ? dayjs(getSurveyStartDate(survey)) : null;
    const endAt = getSurveyEndDate(survey) ? dayjs(getSurveyEndDate(survey)) : null;

    if (survey.status === "closed") {
        return { state: "closed", canSubmit: false, message: "This survey has been closed." };
    }

    if (startAt?.isValid() && startAt.isAfter(now)) {
        return {
            state: "not_started",
            canSubmit: false,
            message: `This survey opens on ${startAt.format("D MMM YYYY, HH:mm")}.`,
        };
    }

    if (endAt?.isValid() && endAt.isBefore(now)) {
        return { state: "closed", canSubmit: false, message: "This survey is no longer accepting responses." };
    }

    return { state: "open", canSubmit: true, message: null };
};

const ExternalSurveyPage = ({ previewMode = false }) => {
    const { token, surveyId } = useParams();
    const [searchParams] = useSearchParams();
    const [surveyRecipient, setSurveyRecipient] = useState(null);
    const [answers, setAnswers] = useState({});
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [invalidQuestionIds, setInvalidQuestionIds] = useState([]);
    const questionRefs = useRef({});

    const survey = surveyRecipient?.survey || null;
    const previewRecipientName = String(searchParams.get("name") || "").trim();
    const respondentName = String(surveyRecipient?.respondent_name || "").trim();
    const surveyAvailability = useMemo(() => getSurveyAvailability(survey), [survey]);
    const isAlreadySubmitted = submitted || surveyRecipient?.status === "responded" || Boolean(surveyRecipient?.responded_at);

    useEffect(() => {
        const loadSurvey = async () => {
            try {
                setLoading(true);

                if (previewMode) {
                    let response;
                    try {
                        response = await nodeAPI.get(`/survey/admin/${surveyId}/external-preview`);
                    } catch (adminError) {
                        const status = adminError.response?.status;
                        if (![401, 403, 404].includes(status)) {
                            throw adminError;
                        }
                        response = await nodeAPI.get(`/survey/external-preview/${surveyId}`);
                    }

                    setSurveyRecipient(buildPreviewSurveyRecipient(response.data.data, previewRecipientName));
                } else {
                    const response = await nodeAPI.get(`/survey/external/${token}`);
                    setSurveyRecipient(response.data.data?.surveyRecipient || null);
                }

                setErrorMessage(null);
            } catch (error) {
                setErrorMessage(error.response?.data?.data?.error || (previewMode ? "Survey preview not found." : "Survey link not found."));
            } finally {
                setLoading(false);
            }
        };

        loadSurvey();
    }, [previewMode, previewRecipientName, surveyId, token]);

    const handleAnswerChange = (questionId, value) => {
        setAnswers((current) => ({
            ...current,
            [questionId]: value,
        }));
        setInvalidQuestionIds((current) => current.filter((id) => id !== questionId));
    };

    const handleOtherTextChange = (questionId, value) => {
        setAnswers((current) => ({
            ...current,
            [getOtherAnswerKey(questionId)]: value,
        }));
        setInvalidQuestionIds((current) => current.filter((id) => id !== questionId));
    };

    const handleSingleChoiceChange = (question, value) => {
        setAnswers((current) => {
            const next = {
                ...current,
                [question.id]: value,
            };
            if (!isOtherOption(value)) {
                delete next[getOtherAnswerKey(question.id)];
            }
            return next;
        });
        setInvalidQuestionIds((current) => current.filter((id) => id !== question.id));
    };

    const handleMultipleChoiceChange = (question, values) => {
        const maxSelections = getMaxSelections(question);
        if (maxSelections && values.length > maxSelections) {
            message.warning(`Please select up to ${maxSelections} option${maxSelections === 1 ? "" : "s"}.`);
            return;
        }
        setAnswers((current) => {
            const next = {
                ...current,
                [question.id]: values,
            };
            if (!values.some(isOtherOption)) {
                delete next[getOtherAnswerKey(question.id)];
            }
            return next;
        });
        setInvalidQuestionIds((current) => current.filter((id) => id !== question.id));
    };

    const getOtherText = (question) => String(answers[getOtherAnswerKey(question.id)] || "").trim();

    const hasMissingOtherText = (question) => {
        const value = answers[question.id];
        const otherSelected = question.question_type === "single_choice"
            ? isOtherOption(value)
            : question.question_type === "multiple_choice" && Array.isArray(value) && value.some(isOtherOption);

        return otherSelected && !getOtherText(question);
    };

    const isQuestionAnswered = (question, value) => {
        if (question.question_type === "rating_0_10") {
            return value !== undefined && value !== null;
        }

        if (question.question_type === "single_choice") {
            return Boolean(value) && !hasMissingOtherText(question);
        }

        if (question.question_type === "multiple_choice") {
            return Array.isArray(value) && value.length > 0 && !hasMissingOtherText(question);
        }

        return Boolean(String(value || "").trim());
    };

    const focusQuestion = (questionId) => {
        const node = questionRefs.current[questionId];
        if (!node) return;

        node.scrollIntoView({ behavior: "smooth", block: "center" });
        window.setTimeout(() => {
            const focusTarget = node.querySelector("input, textarea, button, [role='radio'], .ant-radio-input");
            focusTarget?.focus?.();
        }, 150);
    };

    const buildAnswerPayload = (question) => {
        const value = answers[question.id];
        if (!isQuestionAnswered(question, value)) {
            return null;
        }

        switch (question.question_type) {
            case "rating_0_10":
                return { question_id: question.id, numeric_value: value };
            case "single_choice": {
                const otherText = getOtherText(question);
                return {
                    question_id: question.id,
                    option_value: value,
                    ...(otherText ? { text_value: otherText } : {}),
                };
            }
            case "multiple_choice": {
                const otherText = getOtherText(question);
                return {
                    question_id: question.id,
                    json_value: value,
                    ...(otherText ? { text_value: otherText } : {}),
                };
            }
            default:
                return { question_id: question.id, text_value: String(value || "").trim() };
        }
    };

    const handleSubmit = async () => {
        if (!survey || !surveyAvailability.canSubmit || isAlreadySubmitted) {
            return;
        }

        const questions = survey.survey_question || [];
        const missingOtherTextQuestion = questions.find(hasMissingOtherText);
        if (missingOtherTextQuestion) {
            setInvalidQuestionIds([missingOtherTextQuestion.id]);
            message.error("Please add details for Other.");
            focusQuestion(missingOtherTextQuestion.id);
            return;
        }

        const missingRequiredQuestion = questions.find((question) => (
            question.is_required && !isQuestionAnswered(question, answers[question.id])
        ));

        if (missingRequiredQuestion) {
            setInvalidQuestionIds(
                questions
                    .filter((question) => question.is_required && !isQuestionAnswered(question, answers[question.id]))
                    .map((question) => question.id)
            );
            message.error(`Please answer question ${questions.indexOf(missingRequiredQuestion) + 1}.`);
            focusQuestion(missingRequiredQuestion.id);
            return;
        }

        const overLimitQuestion = questions.find((question) => {
            const maxSelections = getMaxSelections(question);
            const value = answers[question.id];
            return maxSelections && Array.isArray(value) && value.length > maxSelections;
        });

        if (overLimitQuestion) {
            setInvalidQuestionIds([overLimitQuestion.id]);
            message.error(`Please select up to ${getMaxSelections(overLimitQuestion)} options.`);
            focusQuestion(overLimitQuestion.id);
            return;
        }

        setInvalidQuestionIds([]);

        if (previewMode) {
            message.info("Preview only. No response was submitted.");
            return;
        }

        try {
            setSubmitting(true);
            await nodeAPI.post(`/survey/external/${token}/submit`, {
                answers: questions.map(buildAnswerPayload).filter(Boolean),
            });
            setSubmitted(true);
            setSurveyRecipient((current) => current ? {
                ...current,
                status: "responded",
                responded_at: new Date().toISOString(),
            } : current);
            message.success("Survey submitted.");
        } catch (error) {
            message.error(error.response?.data?.data?.error || "Failed to submit survey.");
        } finally {
            setSubmitting(false);
        }
    };

    const renderQuestionInput = (question) => {
        if (isAlreadySubmitted) {
            return null;
        }

        if (question.question_type === "rating_0_10") {
            const currentValue = answers[question.id];
            return (
                <div style={{ width: "100%" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 16, marginBottom: 10, flexWrap: "wrap" }}>
                        <Text type="secondary">0 = Not likely to recommend, 10 = Extremely likely to recommend</Text>
                        {currentValue !== undefined && (
                            <Text strong style={{ whiteSpace: "nowrap" }}>Selected: {currentValue}</Text>
                        )}
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(11, minmax(36px, 1fr))", gap: 8, width: "100%" }}>
                        {RATING_SCALE.map((score) => {
                            const isActive = currentValue === score;
                            const accent = score <= 6 ? "#ef4444" : score <= 8 ? "#f59e0b" : "#16a34a";
                            return (
                                <button
                                    key={score}
                                    type="button"
                                    onClick={() => handleAnswerChange(question.id, score)}
                                    style={{
                                        border: isActive ? `1px solid ${accent}` : "1px solid #d6d3d1",
                                        background: isActive ? accent : "#ffffff",
                                        color: isActive ? "#ffffff" : "#111111",
                                        borderRadius: 8,
                                        minHeight: 42,
                                        fontSize: 14,
                                        fontWeight: 700,
                                        cursor: "pointer",
                                        width: "100%",
                                        transition: "box-shadow 0.16s ease, border-color 0.16s ease, background 0.16s ease",
                                        boxShadow: isActive ? `0 0 0 2px ${accent}22` : "none",
                                    }}
                                    aria-pressed={isActive}
                                    aria-label={`Rate ${score} out of 10`}
                                >
                                    {score}
                                </button>
                            );
                        })}
                    </div>
                </div>
            );
        }

        if (question.question_type === "single_choice") {
            const selectedValue = answers[question.id];
            const otherSelected = isOtherOption(selectedValue);
            return (
                <div style={{ width: "100%" }}>
                    <Radio.Group
                        value={selectedValue}
                        onChange={(event) => handleSingleChoiceChange(question, event.target.value)}
                    >
                        <Space direction="vertical">
                            {getQuestionOptions(question).map((option) => (
                                <Radio key={option} value={option}>{option}</Radio>
                            ))}
                        </Space>
                    </Radio.Group>
                    {otherSelected && (
                        <Input.TextArea
                            rows={3}
                            value={answers[getOtherAnswerKey(question.id)] || ""}
                            onChange={(event) => handleOtherTextChange(question.id, event.target.value)}
                            placeholder="Please specify"
                            style={{ marginTop: 10 }}
                        />
                    )}
                </div>
            );
        }

        if (question.question_type === "multiple_choice") {
            const maxSelections = getMaxSelections(question);
            const selectedValues = answers[question.id] || [];
            const otherSelected = selectedValues.some(isOtherOption);
            return (
                <div style={{ width: "100%" }}>
                    <Checkbox.Group
                        value={selectedValues}
                        onChange={(values) => handleMultipleChoiceChange(question, values)}
                        style={{ width: "100%" }}
                    >
                        <Space direction="vertical" style={{ width: "100%" }}>
                            {getQuestionOptions(question).map((option) => {
                                const isDisabled = maxSelections
                                    && selectedValues.length >= maxSelections
                                    && !selectedValues.includes(option);
                                return (
                                    <Checkbox key={option} value={option} disabled={isDisabled}>
                                        {option}
                                    </Checkbox>
                                );
                            })}
                        </Space>
                    </Checkbox.Group>
                    {otherSelected && (
                        <Input.TextArea
                            rows={3}
                            value={answers[getOtherAnswerKey(question.id)] || ""}
                            onChange={(event) => handleOtherTextChange(question.id, event.target.value)}
                            placeholder="Please specify"
                            style={{ marginTop: 10 }}
                        />
                    )}
                </div>
            );
        }

        if (question.question_type === "short_text") {
            return (
                <Input
                    value={answers[question.id]}
                    onChange={(event) => handleAnswerChange(question.id, event.target.value)}
                    placeholder="Type your answer"
                />
            );
        }

        return (
            <Input.TextArea
                rows={4}
                value={answers[question.id]}
                onChange={(event) => handleAnswerChange(question.id, event.target.value)}
                placeholder="Type your answer"
            />
        );
    };

    if (loading) {
        return (
            <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f5f5f5" }}>
                <Spin />
            </div>
        );
    }

    if (errorMessage || !survey) {
        return (
            <div style={{ minHeight: "100vh", background: "#f5f5f5", padding: 24 }}>
                <div style={{ maxWidth: 760, margin: "0 auto" }}>
                    <Alert type="error" showIcon message={errorMessage || "Survey link not found."} />
                </div>
            </div>
        );
    }

    return (
        <div style={{ minHeight: "100vh", background: "#f5f5f5", padding: "32px 16px" }}>
            <div style={{ maxWidth: 860, margin: "0 auto" }}>
                <Space direction="vertical" size={16} style={{ width: "100%" }}>
                    <div style={{ display: "flex", justifyContent: "center", padding: "8px 0 2px" }}>
                        <img
                            src={logoImage}
                            alt="Mediatropy"
                            style={{ height: 64, width: "auto", objectFit: "contain" }}
                        />
                    </div>
                    <Card className="survey-detail-card survey-detail-card--identified" style={{ background: "#ffffff", borderRadius: 8 }}>
                        <Space direction="vertical" size={16} style={{ width: "100%" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <Title level={3} style={{ margin: 0 }}>{survey.title}</Title>
                                    {respondentName && (
                                        <Paragraph type="secondary" style={{ margin: "8px 0 0" }}>
                                            Questionnaire for <Text strong>{respondentName}</Text>
                                        </Paragraph>
                                    )}
                                    {survey.description && (
                                        <Paragraph type="secondary" style={{ margin: respondentName ? "4px 0 0" : "8px 0 0" }}>
                                            {survey.description}
                                        </Paragraph>
                                    )}
                                </div>
                                <Tag color={isAlreadySubmitted ? "green" : surveyAvailability.canSubmit ? "blue" : "red"}>
                                    {isAlreadySubmitted ? "Submitted" : surveyAvailability.canSubmit ? "Open" : "Closed"}
                                </Tag>
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
                                <div>
                                    <Text type="secondary">Questions</Text>
                                    <div><Text strong>{survey.survey_question?.length || 0}</Text></div>
                                </div>
                                <div>
                                    <Text type="secondary">Deadline</Text>
                                    <div><Text strong>{formatSurveyDateTime(getSurveyEndDate(survey))}</Text></div>
                                </div>
                                <div>
                                    <Text type="secondary">Employee</Text>
                                    <div><Text strong>{respondentName || "-"}</Text></div>
                                </div>
                                <div>
                                    <Text type="secondary">Status</Text>
                                    <div>
                                        <Text strong>
                                            {isAlreadySubmitted
                                                ? "Completed"
                                                : surveyAvailability.canSubmit
                                                    ? "Awaiting response"
                                                    : surveyAvailability.message}
                                        </Text>
                                    </div>
                                </div>
                            </div>
                        </Space>
                    </Card>

                    {isAlreadySubmitted ? (
                        <Alert
                            type="success"
                            showIcon
                            icon={<CheckCircleOutlined />}
                            message="Thank you. Your feedback has been submitted."
                        />
                    ) : previewMode ? (
                        <Alert
                            type="info"
                            showIcon
                            icon={<FormOutlined />}
                            message="Preview only. This is what the former employee will see, but responses are not submitted from this preview."
                        />
                    ) : (
                        <Alert
                            type={surveyAvailability.canSubmit ? "info" : "warning"}
                            showIcon
                            icon={<ClockCircleOutlined />}
                            message={surveyAvailability.message || "Please complete the survey below."}
                        />
                    )}

                    {surveyAvailability.canSubmit && !isAlreadySubmitted && (
                        <Card style={{ background: "#ffffff", borderRadius: 8 }}>
                            <Space direction="vertical" size={20} style={{ width: "100%" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                    <FormOutlined style={{ color: "#262626" }} />
                                    <Title level={5} style={{ margin: 0 }}>Survey Questions</Title>
                                </div>

                                <Form layout="vertical">
                                    {(survey.survey_question || []).map((question, index) => {
                                        const isInvalid = invalidQuestionIds.includes(question.id);
                                        const missingOtherText = hasMissingOtherText(question);

                                        return (
                                            <div
                                                key={question.id}
                                                ref={(node) => {
                                                    questionRefs.current[question.id] = node;
                                                }}
                                                style={{
                                                    padding: 20,
                                                    border: isInvalid
                                                        ? "1px solid #ff4d4f"
                                                        : "1px solid #f0f0f0",
                                                    borderRadius: 8,
                                                    background: isInvalid ? "#fff2f0" : "#ffffff",
                                                    marginBottom: index === survey.survey_question.length - 1 ? 0 : 16,
                                                }}
                                            >
                                                <Form.Item
                                                    label={(
                                                        <span>
                                                            <Text strong>{index + 1}. {question.question_text}</Text>
                                                            {question.is_required && <Text type="danger"> *</Text>}
                                                        </span>
                                                    )}
                                                    validateStatus={isInvalid ? "error" : ""}
                                                    help={getQuestionHelpText(question, isInvalid, missingOtherText)}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    {renderQuestionInput(question)}
                                                </Form.Item>
                                            </div>
                                        );
                                    })}
                                </Form>

                                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                    <Button
                                        variant="primary"
                                        hoverColor="#fad133"
                                        uppercase={false}
                                        type="button"
                                        loading={submitting}
                                        onClick={handleSubmit}
                                    >
                                        Submit Feedback
                                    </Button>
                                </div>
                            </Space>
                        </Card>
                    )}
                </Space>
            </div>
        </div>
    );
};

export default ExternalSurveyPage;
